// src/components/sidebarConfigByCode.js
//
// Resolves the sidebar path/title/icon for a given (moduleName, menuCode) pair
// returned by the backend privileges API.
//
// Resolution order (see `resolveMenuConfig`):
//   1. `overrideByModuleAndCode` - exact "moduleName|menuCode" override (highest priority)
//   2. `configByMenuCode`        - generic mapping by menuCode (case-insensitive fallback)
//   3. Special-cased "lookups" handling
//
// Entries are grouped by feature area. Some backend codes vary across
// environments/releases for the same page - where that's known, all observed
// variants are kept and mapped to the same path/icon.

// ---------------------------------------------------------------------------
// Generic mapping by menuCode (used when no module-specific override exists)
// ---------------------------------------------------------------------------
const configByMenuCode = {
  // Self Service Requests
  REMOTEENTRY: { path: "/remote-work-request", titleEn: "sidebar.remoteWork", icon: "remoteWork" },
  RemoteWorkRequest: { path: "/remote-work-request", titleEn: "sidebar.remoteWork", icon: "remoteWork" },
  LeaveRequest: { path: "/leave-request", titleEn: "sidebar.leave", icon: "leaverequesticon" },
  PermissionRequest: { path: "/self-service-permission-request", titleEn: "sidebar.permission", icon: "permissions" },
  ManualEntryRequest: { path: "/manual-entry-request", titleEn: "sidebar.manualEntry", icon: "manualEntry" },

  // Work Schedule / Shift Planner
  RAMADANPERIOD: { path: "/define-ramdan-period", titleEn: "sidebar.ramadanPeriod", icon: "ramdanperiod" },
  DefineBreakDefinition: { path: "/define-break-definition", titleEn: "sidebar.breakDefinition", icon: "breakmamagementicon" },
  DefineTimetable: { path: "/define-timetable", titleEn: "sidebar.attendanceDuration", icon: "ShiftPlanicon" },
  DefineWorkSchedule: { path: "/define-work-schedule", titleEn: "sidebar.defineWorkSchedule", icon: "ShiftPatternicon" },
  AssignWorkSchedule: { path: "/schedule-assignment", titleEn: "sidebar.scheduleAssignment", icon: "assignshifticon" },
  AssignTemporarySchedule: { path: "/assign-temporary-schedule", titleEn: "sidebar.assignTemporarySchedule", icon: "assigntemporaryshifticon" },

  // Holidays (multiple backend codes observed for the same page)
  HOLIDAYS: { path: "/define-holiday-period", titleEn: "sidebar.holidays", icon: "hoildaysicon" },
  DEFINEPERIOD: { path: "/define-holiday-period", titleEn: "sidebar.holidays", icon: "hoildaysicon" },
  NormalHoliday: { path: "/define-holiday-period", titleEn: "sidebar.holidays", icon: "hoildaysicon" },

  // Employee Management
  DefineEmployee: { path: "/employee", titleEn: "sidebar.employees", icon: "employeesicon" },
  updateBulkEmployee: { path: "/update-bulk-employee", titleEn: "sidebar.updateBulkEmployee", icon: "employeerequest" },
  UpdateSupervisor: { path: "/update-supervisor", titleEn: "sidebar.updateSupervisor", icon: "updatemanager" },
  DefineException: { path: "/employee-exception", titleEn: "sidebar.exception", icon: "exception" },
  GROUPS: { path: "/group-builder", titleEn: "sidebar.groups", icon: "group" },
  RECALCULATEATTENDANCE: { path: "/recalculate-attendance", titleEn: "sidebar.recalculateAttendance", icon: "Recalculateattendance" },

  // Builder / Settings (multiple backend codes observed for the same pages)
  LEAVEBUILDER: { path: "/leave-builder", titleEn: "sidebar.leaveSetting", icon: "leavesettingsicon" },
  LeaveConfiguration: { path: "/leave-builder", titleEn: "sidebar.leaveSetting", icon: "leavesettingsicon" },
  REMOTEWORKSETTING: { path: "/remot-work-builder", titleEn: "sidebar.remoteWorkSetting", icon: "remoteworksettingsicon" },
  RemoteWorkConfiguration: { path: "/remot-work-builder", titleEn: "sidebar.remoteWorkSetting", icon: "remoteworksettingsicon" },
  PermissionSetting: { path: "/permission-type", titleEn: "sidebar.permissionSetting", icon: "permissionsettingsicon" },
  PermissionConfiguration: { path: "/permission-type", titleEn: "sidebar.permissionSetting", icon: "permissionsettingsicon" },
  ManualEntryConfiguration: { path: "/manualentry-builder", titleEn: "sidebar.manualEntrySetting", icon: "manualentrysettingsicon" },
  WorkflowSettting: { path: "/view-work-flow-application-list", titleEn: "sidebar.workFlow", icon: "workflow" },
  NotificationSetting: { path: "/notification-template", titleEn: "sidebar.notification", icon: "notificationicon" },
  FORM: { path: "/view-form-builder", titleEn: "sidebar.form", icon: "form" },
  ReportBuilder: { path: "/new-report-builder", titleEn: "sidebar.newReportBuilder", icon: "form" },
  ApplicationConfiguration: { path: "/application-configuration", titleEn: "sidebar.applicationConfiguration", icon: "applicationconfiguration" },

  // Dashboards
  EmployeeDashboard: { path: "/dashboard", titleEn: "sidebar.dashboard", icon: "dashboard" },
  HrDashboard: { path: "/hr-dashboard", titleEn: "sidebar.hrDashboard", icon: "hrDashboardicon" },
  ManagerDashboard: { path: "/manager-dashboard", titleEn: "sidebar.managerDashboard", icon: "managerDashboardicon" },
  ExecutiveDashboard: { path: "/executive-dashboard", titleEn: "sidebar.executiveDashboard", icon: "executivedashboardicon" },
  AbsenteeismOverview: { path: "/absenteeism-overview", titleEn: "sidebar.absenteeismOverview", icon: "absenteeismOverviewicon" },

  // Integrations
  INTEGRATIONBUILDER: { path: "/integration-builder", titleEn: "sidebar.integrationBuilder", icon: "integrationbuildericon" },
  Monitoring: { path: "/monitoring", titleEn: "sidebar.monitoring", icon: "monitoringicon" },

  // Reports
  "Attendance Report": { path: "/attendance-reports", titleEn: "sidebar.attendanceReport", icon: "attendanceicon" },
  "Violations Reports": { path: "/violations-reports", titleEn: "sidebar.violationsReport", icon: "violationsicon" },
  "Work Schedule Reports": { path: "/work-schedule-report", titleEn: "sidebar.workScheduleReport", icon: "workscheduleicon" },
  "Employee Requests Reports": { path: "/requests-reports", titleEn: "sidebar.employeeRequestsReport", icon: "employeereportsicon" },
  "LineListing Report": { path: "/line-listing-reports", titleEn: "sidebar.customReport", icon: "customreportsiconm" },
  AuditReport: { path: "/audit-report", titleEn: "sidebar.auditing", icon: "auditingicon" },
};

// ---------------------------------------------------------------------------
// Exact "moduleName|menuCode" overrides (take priority over configByMenuCode)
// ---------------------------------------------------------------------------
const overrideByModuleAndCode = {
  // People Management
  "People Management|DefineEmployee": { path: "/employee", titleEn: "sidebar.employees", icon: "employeesetupicon" },
  "People Management|updateBulkEmployee": { path: "/update-bulk-employee", titleEn: "sidebar.updateBulkEmployee", icon: "updatebulkemployeeicon" },
  "People Management|ManualEntryRequest": { path: "/manual-entry", titleEn: "sidebar.manualEntry", icon: "manualentryrequesticon" },
  "People Management|LeaveRequest": { path: "/hr-leave-request", titleEn: "sidebar.leaves", icon: "leaverequesticonmd" },
  "People Management|PermissionRequest": { path: "/permission-request", titleEn: "sidebar.permission", icon: "permissionrequesticonm" },
  "People Management|RemoteWorkRequest": { path: "/hr-remote-work", titleEn: "sidebar.remoteWork", icon: "remoteworkrequesticonmd" },
  "People Management|DefineDelegation": { path: "/define-delegation", titleEn: "sidebar.defineDelegation", icon: "definedelegationicon" },
  "People Management|DefineException": { path: "/employee-exception", titleEn: "sidebar.exception", icon: "defineexceptionsicon" },
  "People Management|Announcement": { path: "/announcement", titleEn: "sidebar.announcement", icon: "announcementicon" },
  "People Management|NotificationTracking": { path: "/notification-tracking", titleEn: "sidebar.notificationTracking", icon: "notificationtrackingicon" },
  "People Management|RECALCULATEATTENDANCE": { path: "/recalculate-attendance", titleEn: "sidebar.recalculateAttendance", icon: "recalculatepageicon" },
  "People Management|ApplicationConfigration": { path: "/application-configuration", titleEn: "sidebar.applicationConfiguration", icon: "applicationconfigurationicon" },
  "People Management|EmployeeLeave": { path: "/EmployeeLeave", titleEn: "sidebar.employeeLeaveReview", icon: "employeeleavereviewicon" },
  "People Management|UserRoleAssignment": { path: "/user-role-assignment", titleEn: "sidebar.userRoleAssignment", icon: "userroleassignmenticon" },
  "People Management|SubordinatesRequests": { path: "/hr-subordinates-requests", titleEn: "sidebar.subordinateRequest", icon: "subordinatesrequesticonm" },
  "People Management|Companies": { path: "/Company", titleEn: "sidebar.company", icon: "companyicon" },
  "People Management|CardPrinting": { path: "/card-print", titleEn: "sidebar.cardPrinting", icon: "cardprintingicon" },
  "People Management|Lookups": { path: "/lookup", titleEn: "sidebar.lookups", icon: "lookups" },

  // Self Services (fallback already covers most of these; kept explicit for clarity)
  "Self Services|RemoteWorkRequest": { path: "/remote-work-request", titleEn: "sidebar.remoteWork", icon: "remoteworkrequesticon" },
  "Self Services|PermissionRequest": { path: "/self-service-permission-request", titleEn: "sidebar.permission", icon: "permissionrequesticon" },
  "Self Services|ManualEntryRequest": { path: "/manual-entry-request", titleEn: "sidebar.manualEntry", icon: "ManualEntryRequesticon" },
  "Self Services|REMOTEENTRY": { path: "/remote-work-request", titleEn: "sidebar.remoteWork", icon: "remoteworkrequesticon" },
  "Self Services|SubordinatesRequests": { path: "/subordinates-requests", titleEn: "sidebar.subordinateRequest", icon: "SubordinatesRequestsicon" },
  "Self Services|Lookups": { path: "/lookup", titleEn: "sidebar.lookups", icon: "lookups" },
};

export function resolveMenuConfig(moduleName, menuCode) {
  const mod = (moduleName || '').trim();
  const code = (menuCode || '').trim();
  const key = `${mod}|${code}`;

  // Try exact override, then generic by code (exact and uppercased)
  let resolved = overrideByModuleAndCode[key]
    || configByMenuCode[code]
    || configByMenuCode[code.toUpperCase()]
    || null;

  // Force any case-variant of 'lookups' to '/lookup'
  const modLc = mod.toLowerCase();
  const codeLc = code.toLowerCase();
  if (!resolved && (modLc === 'lookups' || codeLc === 'lookups')) {
    return { path: '/lookup', titleEn: 'sidebar.lookups', icon: 'lookups' };
  }

  return resolved;
}

export default configByMenuCode;